import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Upload, Loader } from 'lucide-react';
import { formatImageUrl } from '../utils/helpers';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryModal = ({ isOpen, onClose, galleryItem, onSave }) => {
  const [formData, setFormData] = useState({
    caption: '',
    image_url: '',
    order: 0
  });
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (galleryItem) {
      setFormData({
        caption: galleryItem.caption || '',
        image_url: galleryItem.image_url || '',
        order: galleryItem.order || 0
      });
      setImagePreview(galleryItem.image_url || '');
    } else {
      setFormData({
        caption: '',
        image_url: '',
        order: 0
      });
      setImagePreview('');
      setImageFile(null);
    }
  }, [galleryItem, isOpen]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let finalImageUrl = formData.image_url;

      // Upload image if new file selected
      if (imageFile) {
        setUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', imageFile);

        const token = localStorage.getItem('token');
        const uploadResponse = await axios.post(
          `${API_URL}/gallery/upload-image`,
          uploadFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          }
        );

        finalImageUrl = uploadResponse.data.url;
        setUploading(false);
      }

      // Save gallery item
      const saveData = {
        ...formData,
        image_url: finalImageUrl
      };

      onSave(saveData);
    } catch (error) {
      console.error('Failed to upload image or save gallery item:', error);
      alert('Hiba történt a kép feltöltése vagy mentés során');
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="glass rounded-2xl max-w-2xl w-full my-8">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <h2 className="text-2xl font-semibold">
            {galleryItem ? 'Galéria elem szerkesztése' : 'Új galéria elem'}
          </h2>
          <button onClick={onClose} className="text-2xl hover:text-yellow-200 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Kép *</label>
            
            {/* Image Preview */}
            {imagePreview && (
              <div className="mb-4 relative">
                <img
                  src={imagePreview.startsWith('data:') ? imagePreview : formatImageUrl(imagePreview)}
                  alt="Preview"
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            )}

            {/* Upload Button */}
            <label className="btn-magic rounded-lg px-6 py-3 cursor-pointer inline-flex items-center gap-2">
              <Upload className="w-5 h-5" />
              {imagePreview ? 'Kép cseréje' : 'Kép feltöltése'}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </label>
            {!galleryItem && !imageFile && (
              <p className="text-xs text-gray-500 mt-2">Új galéria elemhez kötelező kép feltöltése</p>
            )}
          </div>

          {/* Caption */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Leírás *</label>
            <textarea
              value={formData.caption}
              onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
              required
              rows="3"
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white resize-none"
              placeholder="Add meg a kép leírását..."
            />
          </div>

          {/* Order */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Sorrend</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">Alacsonyabb szám = előrébb jelenik meg</p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-lg transition"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={uploading || (!galleryItem && !imageFile)}
              className="btn-magic rounded-lg px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Feltöltés...
                </>
              ) : (
                galleryItem ? 'Módosítás' : 'Létrehozás'
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .glass {
          background: linear-gradient(180deg, hsl(0 0% 100% / 0.08), hsl(0 0% 100% / 0.04));
          border: 1px solid hsl(0 0% 100% / 0.1);
          backdrop-filter: blur(12px);
        }
      `}</style>
    </div>
  );
};

export default GalleryModal;
